/**
 * Deterministic, SSR-safe helpers that turn a post's markdown into the
 * editorial furniture around it: heading anchors, an "In this article"
 * outline, and an answer-first key-takeaways list.
 */

export interface OutlineItem {
  id: string;
  text: string;
  level: 2 | 3;
}

export function headingSlug(text: string): string {
  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .slice(0, 60)
      .replace(/-+$/, "") || "section"
  );
}

/** Strips inline markdown so headings/bullets read as plain text. */
export function stripInlineMd(s: string): string {
  return s
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[*_`>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function isFenceLine(line: string) {
  return /^\s*```/.test(line);
}

/** Level 2 and 3 headings, with stable unique ids. */
export function extractOutline(md: string): OutlineItem[] {
  const items: OutlineItem[] = [];
  const seen = new Map<string, number>();
  let inFence = false;
  for (const line of (md ?? "").split("\n")) {
    if (isFenceLine(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const m = /^(#{2,3})\s+(.*)$/.exec(line);
    if (!m) continue;
    const text = stripInlineMd(m[2]);
    if (!text) continue;
    const base = headingSlug(text);
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    items.push({
      id: n === 1 ? base : `${base}-${n}`,
      text,
      level: m[1].length === 2 ? 2 : 3,
    });
  }
  return items;
}

/**
 * Key takeaways for the TL;DR block. Prefers an explicit
 * "Key takeaways" / "TL;DR" section, then the first bullet list,
 * and finally the opening sentences.
 */
export function extractTakeaways(md: string, max = 5): string[] {
  const lines = (md ?? "").split("\n");
  const labelled: string[] = [];
  let capturing = false;
  for (const line of lines) {
    if (/^#{2,4}\s/.test(line)) {
      const heading = stripInlineMd(line.replace(/^#{2,4}\s+/, "")).toLowerCase();
      capturing = /key takeaway|tl;?dr|at a glance|quick answer/.test(heading);
      continue;
    }
    if (!capturing) continue;
    const b = /^\s*(?:[-*+]|\d+\.)\s+(.*)$/.exec(line);
    if (b) {
      const t = stripInlineMd(b[1]);
      if (t) labelled.push(t);
    }
  }
  if (labelled.length >= 2) return labelled.slice(0, max);

  const bullets: string[] = [];
  let started = false;
  for (const line of lines) {
    const b = /^\s*(?:[-*+]|\d+\.)\s+(.*)$/.exec(line);
    if (b) {
      const t = stripInlineMd(b[1]);
      if (t) bullets.push(t);
      started = true;
    } else if (started && line.trim() === "" && bullets.length >= 3) {
      break;
    }
    if (bullets.length >= max) break;
  }
  if (bullets.length >= 2) return bullets.slice(0, max);

  const prose = lines
    .filter((l) => l.trim() && !/^[#>\-*|]/.test(l.trim()))
    .join(" ");
  return stripInlineMd(prose)
    .split(/(?<=\.)\s+/)
    .filter((s) => s.length > 40)
    .slice(0, 3);
}

/** First substantive paragraph — used as the quotable direct answer. */
export function extractQuickAnswer(md: string): string {
  const explicit = (md ?? "").match(/(?:^|\n|[.!?]\s+)(?:\*\*)?TL;?DR(?:\*\*)?\s*:\s*([^\n]+)/i);
  if (explicit?.[1]) return stripInlineMd(explicit[1]);

  let inFence = false;
  for (const raw of (md ?? "").split("\n")) {
    if (isFenceLine(raw)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const line = raw.trim();
    if (!line || /^[#>\-*|!]/.test(line) || /^\d+\.\s/.test(line)) continue;
    const text = stripInlineMd(line);
    if (text.length > 60) return text;
  }
  return "";
}

/** Removes presentation-only duplication before rendering an article body. */
export function cleanArticleMarkdown(md: string, title: string): string {
  const normalizedTitle = stripInlineMd(title).toLowerCase();
  const lines = (md ?? "").split("\n");
  const firstContent = lines.findIndex((line) => line.trim().length > 0);
  if (firstContent >= 0) {
    const first = lines[firstContent];
    const candidate = stripInlineMd(first.replace(/^#\s+/, "")).toLowerCase();
    // The page header already renders the H1; drop any leading H1 so a
    // renamed article never shows two headlines.
    if (/^#\s+/.test(first) || candidate === normalizedTitle) lines.splice(firstContent, 1);
  }

  return removeTemplateSection(removeTemplateSection(lines.join("\n"), /^(?:Expert Insight|Expert Insight from Simply Approved Mortgages)$/i), /^(?:More in this series|Explore this topic)$/i)
    .replace(/(?:^|\n|(?<=[.!?])\s+)(?:\*\*)?TL;?DR(?:\*\*)?\s*:\s*[^\n]+/i, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function removeTemplateSection(md: string, heading: RegExp): string {
  const lines = md.split("\n");
  const start = lines.findIndex((line) => {
    const match = /^##\s+(.+?)\s*$/.exec(line.trim());
    return Boolean(match?.[1] && heading.test(stripInlineMd(match[1])));
  });
  if (start < 0) return md;
  let end = start + 1;
  while (end < lines.length && !/^##\s+/.test(lines[end] ?? "")) end++;
  return [...lines.slice(0, start), ...lines.slice(end)].join("\n");
}

/** Extracts a generated company-attributed advisor note for the visual template. */
export function extractExpertInsight(md: string): string | null {
  const lines = (md ?? "").split("\n");
  const start = lines.findIndex((line) => /^##\s+(?:Expert Insight|Expert Insight from Simply Approved Mortgages)\s*$/i.test(line.trim()));
  if (start < 0) return null;
  const body: string[] = [];
  for (let i = start + 1; i < lines.length; i++) {
    const line = lines[i] ?? "";
    if (/^##\s+/.test(line)) break;
    body.push(line);
  }
  const text = stripInlineMd(body.join(" ")).replace(/^Advisor note:\s*/i, "").trim();
  return text || null;
}

export function splitArticleForBanners(markdown: string): [string, string, string] {
  const lines = markdown.split("\n");
  const boundaries = lines
    .map((line, index) => (/^##\s+/.test(line) ? index : -1))
    .filter((index) => index > 0);
  if (boundaries.length < 3) return [markdown, "", ""];
  const firstAt = boundaries[Math.max(1, Math.floor(boundaries.length / 3))] ?? boundaries[1];
  const secondAt = boundaries[Math.max(2, Math.floor((boundaries.length * 2) / 3))] ?? boundaries.at(-1);
  if (firstAt === undefined || secondAt === undefined || secondAt <= firstAt) return [markdown, "", ""];
  return [
    lines.slice(0, firstAt).join("\n"),
    lines.slice(firstAt, secondAt).join("\n"),
    lines.slice(secondAt).join("\n"),
  ];
}

export function blogProductKey(category: string | null, tags: string[]): string {
  const haystack = `${category ?? ""} ${tags.join(" ")}`.toLowerCase();
  if (haystack.includes("construction")) return "construction";
  if (haystack.includes("multifamily") || haystack.includes("commercial")) return "multifamily";
  if (haystack.includes("section 8") || haystack.includes("section-8") || haystack.includes("hap")) return "section-8";
  if (haystack.includes("bridge")) return "bridge";
  if (haystack.includes("flip")) return "flip";
  if (haystack.includes("foreign")) return "foreign";
  if (haystack.includes("portfolio")) return "portfolio";
  return "dscr";
}

/**
 * Pulls the visible "Frequently asked questions" block (### question + answer
 * paragraphs) so FAQPage structured data always mirrors on-page content.
 */
export function extractFaqs(md: string, max = 10): { q: string; a: string }[] {
  const lines = (md ?? "").split("\n");
  const out: { q: string; a: string }[] = [];
  let inFaq = false;
  let q: string | null = null;
  let buf: string[] = [];
  const flush = () => {
    const a = stripInlineMd(buf.join(" "));
    if (q && a) out.push({ q, a });
    q = null;
    buf = [];
  };
  for (const line of lines) {
    const h2 = /^##\s+(.*)$/.exec(line);
    if (h2) {
      if (inFaq) flush();
      inFaq = /frequently asked|faq/i.test(h2[1]);
      continue;
    }
    if (!inFaq) continue;
    const h3 = /^###\s+(.*)$/.exec(line);
    if (h3) {
      flush();
      q = stripInlineMd(h3[1]);
      continue;
    }
    if (q && line.trim()) buf.push(line.trim());
  }
  if (inFaq) flush();
  return out.slice(0, max);
}

/** Schema.org BlogPosting for a published article. */
export function blogPostingJsonLd(opts: {
  url: string;
  headline: string;
  description: string;
  image?: string | null;
  datePublished?: string | null;
  dateModified?: string | null;
  keywords?: string[];
  wordCount?: number;
}) {
  const origin = "https://dscrloanmarket.com";
  const abs = (u: string) => (/^https?:/i.test(u) ? u : `${origin}${u.startsWith("/") ? "" : "/"}${u}`);
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${abs(opts.url)}#article`,
    mainEntityOfPage: abs(opts.url),
    headline: opts.headline.slice(0, 110),
    description: opts.description,
    ...(opts.image ? { image: [abs(opts.image)] } : {}),
    ...(opts.datePublished ? { datePublished: opts.datePublished } : {}),
    dateModified: opts.dateModified ?? opts.datePublished ?? undefined,
    ...(opts.keywords?.length ? { keywords: opts.keywords.join(", ") } : {}),
    ...(opts.wordCount ? { wordCount: opts.wordCount } : {}),
    inLanguage: "en-US",
    author: {
      "@type": "Organization",
      "@id": `${origin}/#organization`,
      name: "Simply Approved Mortgages",
      url: `${origin}/about`,
      description: "Licensed mortgage broker. Company NMLS #2620881, Branch NMLS #2688012.",
      identifier: [
        { "@type": "PropertyValue", propertyID: "NMLS Company ID", value: "2620881" },
        { "@type": "PropertyValue", propertyID: "NMLS Branch ID", value: "2688012" },
      ],
      sameAs: ["https://www.nmlsconsumeraccess.org/EntityDetails.aspx/COMPANY/2620881"],
    },
    reviewedBy: { "@id": `${origin}/#organization` },
    publisher: { "@id": `${origin}/#organization`, "@type": "Organization", name: "Simply Approved Mortgages", url: origin },
  };
}
